import { motion } from 'framer-motion'
import { 
  ExclamationTriangleIcon,
  DocumentTextIcon,
  LinkIcon,
  CogIcon
} from '@heroicons/react/24/outline'

const SupabaseSetup = () => {
  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Setup Instructions */}
      <div className="relative z-10 w-full max-w-2xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="glass-mockup rounded-3xl p-8 text-center"
        >
          {/* Warning Icon */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="w-16 h-16 mx-auto mb-6 glass-badge rounded-2xl flex items-center justify-center"
          >
            <ExclamationTriangleIcon className="w-8 h-8 text-yellow-400" />
          </motion.div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            Database Setup Required
          </h1>
          
          <p className="text-gray-400 mb-8 text-lg">
            ShootSuite needs to be connected to a Supabase database to handle authentication and data storage.
          </p>

          {/* Setup Steps */}
          <div className="space-y-6 text-left">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-start space-x-4 p-4 rounded-xl bg-white/5"
            >
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                1
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Create a Supabase Project</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Go to <a href="https://app.supabase.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">app.supabase.com</a> and create a new project.
                </p>
                <div className="flex items-center space-x-2 text-blue-400">
                  <LinkIcon className="w-4 h-4" />
                  <a href="https://app.supabase.com" target="_blank" rel="noopener noreferrer" className="text-sm hover:underline">
                    Open Supabase Dashboard
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-start space-x-4 p-4 rounded-xl bg-white/5"
            >
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                2
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Set Up Database Schema</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Follow the instructions in SUPABASE_SETUP.md to create the required database tables.
                </p>
                <div className="flex items-center space-x-2 text-purple-400">
                  <DocumentTextIcon className="w-4 h-4" />
                  <span className="text-sm">Check SUPABASE_SETUP.md file</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="flex items-start space-x-4 p-4 rounded-xl bg-white/5"
            >
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                3
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Configure Environment Variables</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Copy <code className="bg-black/30 px-2 py-1 rounded text-blue-300">env.example</code> to <code className="bg-black/30 px-2 py-1 rounded text-blue-300">.env</code> and add your Supabase credentials.
                </p>
                <div className="bg-black/30 rounded-lg p-3 text-sm font-mono text-gray-300">
                  <div>VITE_SUPABASE_URL=your_project_url</div>
                  <div>VITE_SUPABASE_ANON_KEY=your_anon_key</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-start space-x-4 p-4 rounded-xl bg-white/5"
            >
              <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                4
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Restart Development Server</h3>
                <p className="text-gray-400 text-sm mb-3">
                  After setting up your environment variables, restart the development server.
                </p>
                <div className="bg-black/30 rounded-lg p-3 text-sm font-mono text-gray-300">
                  npm run dev
                </div>
              </div>
            </motion.div>
          </div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-8 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20"
          >
            <div className="flex items-center justify-center space-x-2 mb-2">
              <CogIcon className="w-5 h-5 text-blue-400" />
              <h3 className="text-blue-400 font-semibold">Need Help?</h3>
            </div>
            <p className="text-gray-400 text-sm">
              Check the <code className="bg-black/30 px-2 py-1 rounded text-blue-300">SUPABASE_SETUP.md</code> file for detailed setup instructions, 
              or visit the <a href="https://supabase.com/docs" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Supabase documentation</a>.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default SupabaseSetup 